import useSWR, { SWRConfiguration, SWRResponse } from 'swr';
import buildQueryParams from '@utils/query-params';
import axiosInstance from '@api/axios.ts';

type FetcherKey = string | [string, unknown];

interface UseDataFetcherOptions extends SWRConfiguration {
  url: string;
  params?: URLSearchParams;
  shouldFetch?: boolean;
  /**
   * Extra value that separates cache entries without changing the request.
   *
   * Two accounts asking the same URL share one SWR cache entry, one deduping
   * window and one mutation timestamp. That is how signing out and into a
   * different account left the sidebar empty: the new session's mount
   * revalidation was deduped onto the previous account's request and its
   * result then discarded as older than the sign-in cache clear, so the data
   * never arrived and nothing was left to retry.
   *
   * Passing the user id here gives each account its own entry, so one
   * account's session cannot leave the next one holding an empty result, and
   * one account's response can never be shown to another.
   */
  scope?: string | number | null;
}

/**
 * Custom hook to exclusively fetch data from the server.
 * @param options
 */
const useDataFetcher = function useDataFetcher<T>(
  options: UseDataFetcherOptions
): SWRResponse<T, any> {
  const { url, params, shouldFetch = true, scope, ...config } = options;
  const query = buildQueryParams(params);
  const finalUrl = `${url}${query}`;

  const fetcher = async (key: FetcherKey) => {
    const res = await axiosInstance({
      method: 'GET',
      url: Array.isArray(key) ? key[0] : key,
    });
    return res.data;
  };

  // Only callers that ask for a scope get an array key, so every existing
  // caller keeps the plain URL key it has today.
  let key: FetcherKey | null = null;
  if (shouldFetch) {
    key = scope === undefined ? finalUrl : [finalUrl, scope];
  }

  return useSWR<T>(key, fetcher, config);
};

export default useDataFetcher;
